import React, { useMemo } from 'react';
import { ConfigProvider } from 'antd';
import { Tooltip } from '@ui-library/tooltip';
import { useTheme } from 'styled-components';
import { buildNextButtonTheme } from '@ui-library/next/tokens';
import {
  DefaultButton,
  LinkButton,
  NextButton,
  PrimaryButton,
  SecondaryButton,
  ButtonProps,
} from './styles';

export type { ButtonProps } from './styles';

export const Button: React.FC<ButtonProps> = (props) => {
  const {
    icon,
    type = 'primary',
    variant = 'default',
    level = 'primary',
    intent = 'default',
    tooltip: {
      title = '',
      placement = 'top',
      trigger = 'hover',
      show = true,
      ...restTooltipProps
    } = {},
    size: sizeProp,
    noPadding = false,
    ...others
  } = props;

  const { token } = useTheme();
  const size = sizeProp ?? (variant === 'next' ? 'middle' : 'large');
  const nextButtonTheme = useMemo(() => buildNextButtonTheme(token.next), [token.next]);

  if (variant === 'next') {
    const antTypeMap = { primary: 'primary', secondary: 'default', tertiary: 'text' } as const;
    const antType = antTypeMap[level];

    const nextButton = (
      <NextButton
        {...others}
        type={antType}
        $level={level}
        $intent={intent}
        size={size === 'xlarge' ? 'large' : size}
        icon={icon}
      >
        {props.name ?? others.children}
      </NextButton>
    );

    return title && show ? (
      <ConfigProvider theme={nextButtonTheme}>
        <Tooltip title={title} placement={placement} trigger={trigger} {...restTooltipProps}>
          {nextButton}
        </Tooltip>
      </ConfigProvider>
    ) : (
      <ConfigProvider theme={nextButtonTheme}>{nextButton}</ConfigProvider>
    );
  }

  const comp = (() => {
    const danger = variant === 'danger' || !!others.danger;

    if (type === 'secondary') {
      return (
        <SecondaryButton {...others} variant={variant} size={size} icon={icon}>
          {props.name ?? others.children}
        </SecondaryButton>
      );
    }

    if (type === 'text' || type === 'link') {
      return (
        <LinkButton
          {...others}
          noPadding={noPadding}
          type={type}
          danger={danger}
          size={size}
          icon={icon}
        >
          {props.name ?? others.children}
        </LinkButton>
      );
    }

    if (type === 'primary') {
      return (
        <PrimaryButton
          {...others}
          type="primary"
          variant={variant}
          danger={danger}
          size={size}
          icon={icon}
        >
          {props.name ?? others.children}
        </PrimaryButton>
      );
    }

    return (
      <DefaultButton {...others} type="default" variant={variant} size={size} icon={icon}>
        {props.name ?? others.children}
      </DefaultButton>
    );
  })();

  if (title && show) {
    return (
      <Tooltip title={title} placement={placement} trigger={trigger} {...restTooltipProps}>
        {comp}
      </Tooltip>
    );
  }

  return comp;
};
